import React from "react";
import { LoadingSpinner } from "@/components/bnp-icons";

interface UploadDropzoneProps {
  acceptedFormats: string[];
  maxSizeMB: number;

  isDragging: boolean;

  uploading: boolean;
  uploadProgress: number;
  currentFileIndex: number;
  totalFiles: number;

  processingMessage: string;

  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;

  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;

  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;

  onFileChange: (files: FileList | null) => void;
}

const UploadDropzone: React.FC<UploadDropzoneProps> = ({
  acceptedFormats,
  maxSizeMB,
  isDragging,
  uploading,
  uploadProgress,
  currentFileIndex,
  totalFiles,
  processingMessage,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileChange,
}) => {
  return (
    <div
      className={`relative border-2 border-dashed rounded-xl p-10 text-center transition-colors
      ${
        isDragging
          ? "border-bnp-cyan bg-cyan-50"
          : "border-gray-300 hover:border-bnp-cyan"
      }`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <input
        type="file"
        multiple
        accept={acceptedFormats.join(",")}
        className="absolute inset-0 opacity-0 cursor-pointer"
        onChange={(e) => onFileChange(e.target.files)}
        disabled={uploading}
      />

      {uploading && (
        <div className="absolute inset-x-0 top-0 bg-bnp-cyan text-white rounded-t-xl p-4 space-y-2">
          <div className="flex items-center justify-center gap-2">
            <LoadingSpinner />
            <span className="text-sm font-medium">
              {processingMessage || "Uploading..."}
            </span>
          </div>

          {totalFiles > 1 && (
            <p className="text-xs text-white/80 text-center">
              File {currentFileIndex + 1} of {totalFiles}
            </p>
          )}

          <div className="w-full h-2 bg-white/25 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-200 ease-out"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>

          <p className="text-xs text-white/80 text-center">{uploadProgress}%</p>
        </div>
      )}

      <svg
        className="w-14 h-14 mx-auto text-gray-400 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
        />
      </svg>

      <h3 className="text-lg font-semibold text-gray-900">
        Upload Images or Videos
      </h3>

      <p className="mt-2 text-gray-500">
        Drag &amp; drop files here or click to browse
      </p>

      <p className="mt-3 text-sm text-gray-400">
        {acceptedFormats.map((x) => x.split("/")[1].toUpperCase()).join(", ")}
        {" • "}
        Max {maxSizeMB} MB
      </p>
    </div>
  );
};

export default UploadDropzone;
